<script setup>
import CustomFieldValue from "@/components/customFields/CustomFieldValue.vue";
import VClamp from "vue-clamp";
import {computed, inject, provide, ref, watch} from "vue";
import {trackerTypes} from "@/constants/trackers";
import constants from "@/constants/constants";
import trackersService from "@/services/trackers.service";
import AccessSettings from "@/components/trackers/EcosystemsAccessSettings.vue";
import {useStore} from "vue2-helpers/vuex";
import EcosystemsSharing from "@/views/trackers/tracker/EcosystemsSharing.vue";
import {trackerAccess} from "@/helpers/string";

const store = useStore();
const tracker = inject('tracker');
const isProject = inject('isProject', false);
const trackerInfoChanged = inject('trackerInfoChanged', null);
const showModal = ref(false);
const newStatus = ref(tracker.value.status);
const accessSettings = ref(null);

const showStatusSelect = computed(() => tracker.value.type === trackerTypes.PUBLIC && tracker.value.user_permissions.includes('tracker-change-status'));
const showAccessSettings = computed(() => tracker.value.type === trackerTypes.PRIVATE && store.getters.isEnterprise);

const showInfo = computed(() =>
    tracker.value.description.length ||
    showStatusSelect.value ||
    tracker.value.categories?.length ||
    tracker.value.start_date ||
    tracker.value.submitter_fields?.length ||
    (!isProject && trackerAccess(tracker.value) !== 'None')
);

const hasModal = computed(() => {
    let itemsCount = 0;

    if (tracker.value.raw_description.length) {
        return true;
    }

    if (showStatusSelect.value) {
        ++itemsCount;
    }

    if (tracker.value.categories?.length) {
        ++itemsCount;
    }

    if (tracker.value.start_date || tracker.value.end_date) {
        ++itemsCount;
    }

    if (showAccessSettings.value) {
        ++itemsCount;
    }

    if (tracker.value.submitter_fields?.length) {
        itemsCount += tracker.value.submitter_fields.length;
    }

    return itemsCount > 1;
});

watch(newStatus, () => {
    updateStatus();
})

async function updateStatus() {
    await trackersService.update(tracker.value.id, {status: newStatus.value});
    trackerInfoChanged();
}

async function accessSettingsChanged(params) {
    await trackersService.saveAccessSettings(tracker.value.id, params);
    trackerInfoChanged();
}

function openAccessSettings() {
    accessSettings.value.open(tracker.value.access_type, tracker.value.ecosystems);
}

provide('openAccessSettings', openAccessSettings);
</script>

<template>
    <div v-if="showInfo" class="sidebar-manage__plate sidebar-manage__plate--info">
        <div class="sidebar-manage__plate__expanded">
            <div class="sidebar-manage__plate__inner">
                <b v-if="tracker.description.length">
                    <v-clamp
                        autoresize
                        :max-lines="3"
                        class="mb-2"
                    >
                        {{tracker.description}}
                    </v-clamp>
                </b>

                <div v-else-if="showStatusSelect">
                    <div class="mb-2"><b>Status</b></div>

                    <select
                        v-if="showStatusSelect"
                        v-model="newStatus"
                        :class="{'status-select--live': newStatus === 'live'}"
                        class="status-select mb-3"
                    >
                        <option
                            v-for="(name, key) in constants.trackerStatus"
                            :key="key"
                            :value="key"
                            :class="name"
                        >
                            {{ name }}
                        </option>
                    </select>
                </div>

                <template v-else-if="tracker.categories?.length">
                    <div class="d-flex mb-2">
                        <div class="feature-title mr-1"><b>Categories:</b></div>
                        <div class="feature-value">{{ tracker.categories.join(', ') }}</div>
                    </div>
                </template>

                <template v-else-if="tracker.start_date || tracker.end_date">
                    <template v-if="tracker.start_date">
                        <div class="d-flex mb-2">
                            <div class="feature-title mr-1"><b>Start Date:</b></div>
                            <div class="feature-value">{{ tracker.start_date }}</div>
                        </div>
                    </template>

                    <template v-if="tracker.end_date">
                        <div class="d-flex mb-2">
                            <div class="feature-title mr-1"><b>End Date:</b></div>
                            <div class="feature-value">{{ tracker.end_date }}</div>
                        </div>
                    </template>
                </template>

                <EcosystemsSharing v-else-if="showAccessSettings && trackerAccess(tracker) !== 'None'"/>

                <template v-else-if="tracker.submitter_fields?.length">
                    <div class="d-flex flex-wrap align-items-center mb-2">
                        <div class="feature-title mr-1"><b>{{ tracker.submitter_fields[0].field.name }}:</b></div>
                        <div class="feature-value">
                            <CustomFieldValue
                                :field="tracker.submitter_fields[0].field"
                                :init-value="tracker.submitter_fields[0].value"
                                :editable="false"
                                :collapse-text="true"
                                :from-tracker-panel="true"
                            />
                        </div>
                    </div>
                </template>

                <a
                    v-if="hasModal"
                    class="sidebar-manage__plate__footer"
                    @click="showModal = true"
                >
                    More Tracker Info
                </a>

                <b-modal v-model="showModal" hide-header hide-footer :modal-class="{'modal-with-sidebar': true, 'narrow': !tracker.raw_description.length}">
                    <div class="tracker-info">
                        <div v-if="tracker.raw_description.length">
                            <header class="modal-header">
                                <h5 class="modal-title">Tracker Info</h5>
                            </header>

                            <div v-html="tracker.raw_description"/>
                        </div>

                        <div class="tracker-info__sidebar">
                            <button @click="showModal = false" type="button" aria-label="Close" class="close">×</button>

                            <div class="tracker-info__sidebar__inner">
                                <select
                                    v-if="showStatusSelect"
                                    v-model="newStatus"
                                    :class="{'status-select--live': newStatus === 'live'}"
                                    class="status-select mb-3"
                                >
                                    <option
                                        v-for="(name, key) in constants.trackerStatus"
                                        :key="key"
                                        :value="key"
                                        :class="name"
                                    >
                                        {{ name }}
                                    </option>
                                </select>

                                <template v-if="tracker.categories?.length">
                                    <div class="feature-title">Categories:</div>
                                    <div class="feature-value">{{ tracker.categories.join(', ') }}</div>
                                </template>

                                <template v-if="tracker.start_date">
                                    <div class="feature-title">Start Date:</div>
                                    <div class="feature-value">{{ tracker.start_date }}</div>
                                </template>

                                <template v-if="tracker.end_date">
                                    <div class="feature-title">End Date:</div>
                                    <div class="feature-value">{{ tracker.end_date }}</div>
                                </template>

                                <div v-for="(field, index) in tracker.submitter_fields" :key="index">
                                    <div class="feature-title">{{ field.field.name }}:</div>
                                    <div class="feature-value">
                                        <CustomFieldValue
                                            :field="tracker.submitter_fields[index].field"
                                            :init-value="tracker.submitter_fields[index].value"
                                            :editable="false"
                                        />
                                    </div>
                                </div>

                                <EcosystemsSharing v-if="showAccessSettings"/>
                            </div>
                        </div>
                    </div>
                </b-modal>
            </div>
        </div>

        <AccessSettings
            v-if="store.getters.isEnterprise && tracker.type === trackerTypes.PRIVATE && tracker.user_permissions.includes('tracker-add-ecosystem')"
            ref="accessSettings"
            :from-tracker-page="true"
            :init-access-type="tracker.access_type"
            :init-ecosystems="tracker.ecosystems"
            @saved="accessSettingsChanged"
        />
    </div>
</template>

<style scoped>
    
</style>
